import { uniq } from 'lodash-es';
import { normalize } from 'normalizr';

import { EndpointError } from '@iconik-io/iconik-api/src/client/response';
import {
  type AssetsApprovalBySchema,
  type AssetsApprovalSchema,
  type AssetsApprovalSchemaNullable,
  type AssetsShareCreateSchema,
  type AssetsShareUserSchema,
  httpDeleteByObjectTypeByObjectIdApprovals,
  httpDeleteByObjectTypeByObjectIdApprovalsExternalByEmail,
  httpDeleteByObjectTypeByObjectIdApprovalsRequest,
  httpDeleteByObjectTypeByObjectIdApprovalsUserByUserId,
  httpGetByObjectTypeByObjectIdApprovals,
  httpGetByObjectTypeByObjectIdApprovalsRequest,
  httpGetByObjectTypeByObjectIdSharesByShareId,
  httpGetByObjectTypeByObjectIdVersionsByVersionIdApprovals,
  httpGetByObjectTypeByObjectIdVersionsByVersionIdApprovalsRequest,
  httpPatchByObjectTypeByObjectIdApprovalsRequest,
  httpPostByObjectTypeByObjectIdApprovalsRequest,
  httpPutByObjectTypeByObjectIdApprovals,
} from '@iconik-io/iconik-api/src/services/assets';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import { entitySchemas } from '@iconik-io/shared-lib/src/redux/entities/schemas';
import { selectLatestActiveVersionId } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import {
  type AssetApprovalStatus,
  REVIEW_STATUSES,
} from '../../constants/reviewapproval';
import type { AppDispatch, GetState } from '../../store/types';
import { selectUserRoles } from '../../utils/get_user_roles';
import { addDefaultShareSettings } from '../../utils/set_default_share_settings';
import { segmentTrackEvent } from '../analytics_tracking/thunks';
import { errorMessage, successMessage } from '../messages/thunks';
import { approveRejectBulkRequest } from '../pages/asset/thunks';
import { createShare, deleteSharesByIds } from '../shares/thunks';
import { selectShareUsersByShareId } from '../shares_users/selectors';
import { deleteShareUsers } from '../shares_users/thunks';
import { setAssetApprovals, setAssetReviewRequest } from './slice';

export const fetchAssetReviewRequest =
  (assetId: string, versionId?: string) => (dispatch: AppDispatch) =>
    dispatch(
      versionId
        ? httpGetByObjectTypeByObjectIdVersionsByVersionIdApprovalsRequest(
            OBJECT_TYPE_ASSETS,
            assetId,
            versionId,
          )
        : httpGetByObjectTypeByObjectIdApprovalsRequest(
            OBJECT_TYPE_ASSETS,
            assetId,
          ),
    ).then(({ body }) => {
      const key = `${body.object_id}_${body.version_id}`;
      dispatch(setAssetReviewRequest({ key, request: body }));
      if (body.users_info) {
        const { entities } = normalize(body.users_info, [
          entitySchemas.users_basic,
        ]);
        dispatch(updateEntities(entities));
      }

      return body;
    });

export const fetchAssetApprovals =
  (assetId: string, versionId: string | null | undefined) =>
  (dispatch: AppDispatch, getState: GetState) =>
    dispatch(
      versionId
        ? httpGetByObjectTypeByObjectIdVersionsByVersionIdApprovals(
            OBJECT_TYPE_ASSETS,
            assetId,
            versionId,
          )
        : httpGetByObjectTypeByObjectIdApprovals(OBJECT_TYPE_ASSETS, assetId),
    ).then((response) => {
      const state = getState();
      const latestActiveVersionId = selectLatestActiveVersionId(state, assetId);
      const version = versionId || latestActiveVersionId;
      const key = `${assetId}_${version}`;

      const approvals = response.body.objects || [];
      dispatch(setAssetApprovals({ key, approvals }));

      dispatch(
        updateEntities({
          search_items: {
            [assetId]: {
              // biome-ignore lint/plugin: the elastic approvals are nullable where the entity has them required, and nothing normalizes in between.
              approval: { approvals_by: approvals as AssetsApprovalBySchema[] },
            },
          },
        }),
      );
      return approvals;
    });

export const fetchAssetReviewApprovals =
  (assetId: string, versionId?: string) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const latestActiveVersionId = selectLatestActiveVersionId(state, assetId);
    const versionIdToFetch = versionId || latestActiveVersionId;

    dispatch(fetchAssetReviewRequest(assetId, versionIdToFetch)).then(
      (reviewRequest) => {
        if (reviewRequest.share_id) {
          dispatch(
            httpGetByObjectTypeByObjectIdSharesByShareId(
              OBJECT_TYPE_ASSETS,
              assetId,
              reviewRequest.share_id,
            ),
          );
        }
        if (reviewRequest.object_id) {
          dispatch(
            fetchAssetApprovals(
              reviewRequest.object_id,
              reviewRequest.version_id,
            ),
          );
        }
      },
    );
  };

export const createAssetReviewRequest =
  (assetId: string, data: AssetsApprovalSchema) => (dispatch: AppDispatch) =>
    dispatch(
      httpPostByObjectTypeByObjectIdApprovalsRequest(
        OBJECT_TYPE_ASSETS,
        assetId,
        data,
      ),
    ).then((response) => {
      const key = `${response.body.object_id}_${response.body.version_id}`;
      dispatch(
        setAssetReviewRequest({
          key,
          request: response.body,
        }),
      );
    });

export const createAssetReviewRequestWithShare =
  (
    assetId: string,
    data: AssetsApprovalSchema,
    shareData: AssetsShareCreateSchema,
  ) =>
  (dispatch: AppDispatch) =>
    dispatch(
      createShare('assets', assetId, {
        ...shareData,
        is_approval: true,
        automatic_approval_share: true,
      }),
    ).then((response) =>
      dispatch(
        createAssetReviewRequest(assetId, {
          ...data,
          share_id: response.body.id,
        }),
      ),
    );

export const addShareUsersToReview =
  (
    shareUsers: AssetsShareUserSchema[],
    approvalsRequest: AssetsApprovalSchemaNullable,
  ) =>
  (dispatch: AppDispatch) => {
    const objectId = approvalsRequest.object_id;
    if (!objectId) {
      throw new Error('Object id is missing in the approvals request');
    }

    const newExternal = shareUsers
      .filter((shareUser) => !shareUser.internal_user_id)
      .map((shareUser) => shareUser.email);
    const newUsers = shareUsers
      .map((shareUser) => shareUser.internal_user_id)
      .filter((userId): userId is string => Boolean(userId));
    const minNumber = approvalsRequest.min_number ?? 1;

    const externals = uniq([
      ...(approvalsRequest.externals ?? []),
      ...newExternal,
    ]);
    const users = uniq([...(approvalsRequest.users ?? []), ...newUsers]);
    const min_number = minNumber > 1 ? externals.length + users.length : 1;
    return dispatch(
      updateAssetReviewRequest(objectId, {
        externals,
        users,
        min_number,
      }),
    );
  };

export const updateAssetReviewRequest =
  (assetId: string, data: AssetsApprovalSchema) => (dispatch: AppDispatch) =>
    dispatch(
      httpPatchByObjectTypeByObjectIdApprovalsRequest(
        OBJECT_TYPE_ASSETS,
        assetId,
        data,
      ),
    ).then((response) => {
      const key = `${response.body.object_id}_${response.body.version_id}`;
      dispatch(
        setAssetReviewRequest({
          key,
          request: response.body,
        }),
      );
      return response.body;
    });

export const clearAssetReviewRequest =
  (assetId: string) => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const latestActiveVersionId = selectLatestActiveVersionId(state, assetId);
    const key = `${assetId}_${latestActiveVersionId}`;

    dispatch(setAssetReviewRequest({ key, request: undefined }));
    dispatch(setAssetApprovals({ key, approvals: [] }));
  };

export const deleteAssetReviewRequest =
  (assetId: string) => (dispatch: AppDispatch, getState: GetState) =>
    dispatch(
      httpDeleteByObjectTypeByObjectIdApprovalsRequest(
        OBJECT_TYPE_ASSETS,
        assetId,
      ),
    ).then(() => {
      const state = getState();
      const latestActiveVersionId = selectLatestActiveVersionId(state, assetId);
      const { assetReviewRequests } = getState().assetReviewApprovalsReducer;
      const key = `${assetId}_${latestActiveVersionId}`;
      const assetReview = assetReviewRequests[key];

      if (assetReview && assetReview.share_id) {
        dispatch(
          deleteSharesByIds(OBJECT_TYPE_ASSETS, assetId, [
            assetReview.share_id,
          ]),
        );
      }

      dispatch(clearAssetReviewRequest(assetId));
    });

export const setReviewStatusWhenNoReview =
  (assetId: string, status: AssetApprovalStatus) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const userId = getState().authReducer.userData.id;
    const settings = getState().settings.merged.default_share_options;
    const userRoles = selectUserRoles(getState());

    const submitData = addDefaultShareSettings(
      {},
      true,
      false,
      userRoles,
      settings,
    );
    const shareData: AssetsShareCreateSchema = {
      ...submitData,
      allow_setting_approve_status: true,
      is_approval: true,
      emails: [],
      // This value is added intentionally to work with workaround for creating shares
      // to create an approval, this value is added to go inline with share settings
      password: 'any_password',
      automatic_approval_share: true,
    };

    // Browse only users can approve/reject without request, but can't write shares
    if (
      !userRoles.canWriteShares &&
      (status === REVIEW_STATUSES.APPROVED ||
        status === REVIEW_STATUSES.NOT_APPROVED)
    ) {
      return dispatch(
        approveRejectBulkRequest([assetId], OBJECT_TYPE_ASSETS, status),
      ).then(() => dispatch(fetchAssetReviewApprovals(assetId)));
    }

    return dispatch(
      createAssetReviewRequestWithShare(
        assetId,
        {
          status,
          users: userId ? [userId] : [],
        },
        shareData,
      ),
    ).then(() =>
      dispatch(
        httpPutByObjectTypeByObjectIdApprovals(OBJECT_TYPE_ASSETS, assetId, {
          status,
        }),
      ).then(() => dispatch(fetchAssetReviewApprovals(assetId))),
    );
  };

export const setReviewStatus =
  (assetId: string, status: AssetApprovalStatus) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const latestActiveVersionId = selectLatestActiveVersionId(state, assetId);
    const { assetReviewRequests } = getState().assetReviewApprovalsReducer;
    const assetReviewRequest =
      assetReviewRequests[`${assetId}_${latestActiveVersionId}`];
    dispatch(
      segmentTrackEvent({
        eventName: 'Set Approval',
        properties: {
          Status: status,
        },
      }),
    );

    if (!assetReviewRequest) {
      return dispatch(setReviewStatusWhenNoReview(assetId, status));
    }

    return dispatch(
      httpPutByObjectTypeByObjectIdApprovals(OBJECT_TYPE_ASSETS, assetId, {
        status,
      }),
    ).then(() => {
      if (status === REVIEW_STATUSES.APPROVED) {
        dispatch(successMessage({ text: 'You approved this asset' }));
      } else if (status === REVIEW_STATUSES.NOT_APPROVED) {
        dispatch(errorMessage({ text: 'You rejected this asset' }));
      }
      return dispatch(fetchAssetReviewApprovals(assetId));
    });
  };

export const deleteCurrentUserReviewStatus =
  (assetId: string) => (dispatch: AppDispatch) =>
    dispatch(
      httpDeleteByObjectTypeByObjectIdApprovals(OBJECT_TYPE_ASSETS, assetId),
    ).then(() => dispatch(fetchAssetReviewApprovals(assetId)));

export const deleteUserFromReview =
  (assetReviewRequest: AssetsApprovalSchemaNullable, userId: string) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const assetId = assetReviewRequest.object_id;
    if (!assetId) {
      return Promise.resolve();
    }
    if (assetReviewRequest?.users) {
      const users = assetReviewRequest.users.filter((user) => user !== userId);
      const update: AssetsApprovalSchema = { users };
      const minNumber = assetReviewRequest.min_number ?? 1;
      if (minNumber > 1 && users.length !== assetReviewRequest.users.length) {
        update.min_number = minNumber - 1;
      }
      await dispatch(updateAssetReviewRequest(assetId, update));
    }

    // Now check if we have a share user with this user id and if so, delete it as well.
    if (assetReviewRequest.share_id) {
      const shareUsers = selectShareUsersByShareId(
        getState(),
        assetReviewRequest.share_id,
      );
      const shareUser = shareUsers.find(
        (user) => user.internal_user_id === userId,
      );
      if (shareUser) {
        await dispatch(deleteShareUsers([shareUser]));
      }
    }

    return dispatch(
      httpDeleteByObjectTypeByObjectIdApprovalsUserByUserId(
        OBJECT_TYPE_ASSETS,
        assetId,
        userId,
      ),
    ).catch((error) => {
      // 404 is fine since that just means that this user has not set an approval status yet
      if (error instanceof EndpointError) {
        if (error.response.status === 404) {
          return Promise.resolve();
        }
      }
      throw error;
    });
  };

export const deleteExternalUserFromReview =
  (assetReviewRequest: AssetsApprovalSchemaNullable, email: string) =>
  async (dispatch: AppDispatch, getState: GetState) => {
    const assetId = assetReviewRequest.object_id;
    if (!assetId) {
      return Promise.resolve();
    }
    if (assetReviewRequest?.externals) {
      const externals = assetReviewRequest.externals.filter(
        (user) => user !== email,
      );
      const update: AssetsApprovalSchema = { externals };
      const minNumber = assetReviewRequest.min_number ?? 1;
      if (
        minNumber > 1 &&
        externals.length !== assetReviewRequest.externals.length
      ) {
        update.min_number = minNumber - 1;
      }
      await dispatch(updateAssetReviewRequest(assetId, update));
    }

    // Now check if we have a share user with this email and if so, delete it as well.
    if (assetReviewRequest.share_id) {
      const shareUsers = selectShareUsersByShareId(
        getState(),
        assetReviewRequest.share_id,
      );
      const shareUser = shareUsers.find((user) => user.email === email);
      if (shareUser) {
        await dispatch(deleteShareUsers([shareUser]));
      }
    }

    return dispatch(
      httpDeleteByObjectTypeByObjectIdApprovalsExternalByEmail(
        OBJECT_TYPE_ASSETS,
        assetId,
        email,
      ),
    ).catch((error) => {
      // 404 is fine since that just means that this user has not set an approval status yet
      if (error instanceof EndpointError) {
        if (error.response.status === 404) {
          return Promise.resolve();
        }
      }
      throw error;
    });
  };
